import {
  analyzeActualEpisode,
  analyzeSeasonalPanel,
  analyzeWindowSensitivity,
  augmentedDickeyFuller,
  attributeFxAdjustedPriceChange,
  available,
  calculateSeasonalZScores,
  decomposeMatifPair,
  engleGrangerCointegration,
  estimateAr1HalfLife,
  logReturnCorrelation,
  mean,
  median,
  quantile,
  runActualContractBacktest,
  sampleStandardDeviation,
  studyForwardReversion,
  notAvailable,
  type ActualBacktestEpisode,
  type ActualEpisodeAnalysis,
  type ActualEpisodePricePoint,
  type ForwardReversionPoint,
  type MetricResult,
  type SeasonalValueEpisode,
  type SeasonalZPoint,
} from "../analytics";
import type {
  AnalysisConfig,
  DailySettlement,
  DataCoverage,
  FuturesContract,
  Instrument,
  MarketDataAdapter,
} from "../data";
import { AdapterValidationError, FX_QUALITY_FLAGS, isSyntheticSourceId } from "../data";
import {
  alignSameDateIntersection,
  enabledMonthsForLeg,
  flagConsecutiveStalePrices,
  formatContract,
  getPairPreset,
  getSeasonalityWindow,
  normalizeToCadPerMetricTonne,
  PAIR_PRESET_LIST,
  QUALITY_FLAGS,
  resolveAutoMapping,
  toSeasonalDayKey,
  type ActualContractPair,
  type Currency,
  type DisplayMetric,
  type EnabledCanolaMonthCode,
  type FuturesMonthCode,
  type ISODate,
  type InstrumentId,
  type QualityFlag,
  type RawPrice,
} from "../domain";
import type {
  ContractChoice,
  DashboardMetric,
  ForwardStudyRow,
  LeadLagRow,
  PairDashboardFixture,
  RegimePoint,
  SeasonalAveragePoint,
  SeasonalSeries,
  SpreadPoint,
  StrategyPoint,
  WindowSensitivityRow as UiWindowSensitivityRow,
} from "../features/dashboard/types";
import type {
  ApplicationCatalog,
  ApplicationDataProvenance,
  DashboardAnalysisControls,
  DashboardAnalysisDiagnostics,
  DashboardAnalysisRequest,
  DashboardAnalysisResult,
  DashboardCurrentKnowledgeDiagnostics,
  DashboardCurrentKnowledgeResult,
  DashboardPairCatalogEntry,
} from "./types";

const KNOWN_QUALITY_FLAGS = new Set<QualityFlag>(QUALITY_FLAGS);
const KNOWN_FX_QUALITY_FLAGS = new Set<string>(FX_QUALITY_FLAGS);

const UI_WINDOWS = [20, 40, 60, 90, 120, 180, 252] as const;
const CURRENT_KNOWLEDGE_FORMAL_UNAVAILABLE = {
  status: "not-available" as const,
  reason: "historical-point-in-time-unavailable" as const,
  detail: "The R2 release does not retain the revision that was known on each historical trade date. The research backtest therefore uses the latest released history and is not a point-in-time revision reconstruction.",
};

type AnalysisScope = "formal-walk-forward" | "current-knowledge";
type ProjectionDiagnostics = DashboardAnalysisDiagnostics | DashboardCurrentKnowledgeDiagnostics;

export interface DashboardApplicationServiceContext {
  readonly adapter: MarketDataAdapter;
  readonly catalog: ApplicationCatalog;
  readonly source: {
    readonly mode: "demo" | "production";
    readonly sourceId: string;
    readonly generatedAt: string;
    readonly declaredAsOf: ISODate;
    readonly synthetic: boolean;
  };
}

interface EpisodeBundle {
  readonly pair: ActualContractPair;
  readonly legAContract: FuturesContract;
  readonly legBContract: FuturesContract;
  readonly points: readonly ActualEpisodePricePoint[];
  readonly excludedCount: number;
  readonly dateStart: ISODate;
  readonly dateEnd: ISODate;
  readonly matifFxInput?: Parameters<typeof attributeFxAdjustedPriceChange>[0];
}

interface EpisodeAnalysisBundle {
  readonly episode: EpisodeBundle;
  readonly analysis: ActualEpisodeAnalysis;
  readonly seasonal: SeasonalValueEpisode;
  readonly seasonalZ: readonly SeasonalZPoint[];
}

function asOfTimestamp(asOf: ISODate): string {
  return `${asOf}T23:59:59Z`;
}

function validateControls(controls: DashboardAnalysisControls): void {
  if (!Number.isInteger(controls.window) || controls.window < 10) {
    throw new Error("Analysis window must be an integer of at least 10 observations.");
  }
  if (!Number.isFinite(controls.entryZ) || controls.entryZ <= 0) {
    throw new Error("Entry Z threshold must be positive.");
  }
  if (
    !Number.isFinite(controls.exitZ) ||
    controls.exitZ < 0 ||
    controls.exitZ >= controls.entryZ
  ) {
    throw new Error("Exit Z threshold must be non-negative and below the entry threshold.");
  }
}

function recognizedQualityFlags(
  flags: readonly string[],
  failOnUnknown: boolean,
): readonly QualityFlag[] {
  const unknown = flags.filter((flag) => !KNOWN_QUALITY_FLAGS.has(flag as QualityFlag));
  if (failOnUnknown && unknown.length > 0) {
    throw new AdapterValidationError(
      `Production settlement contains unsupported quality flags: ${unknown.join(", ")}.`,
    );
  }
  return flags.filter((flag): flag is QualityFlag => KNOWN_QUALITY_FLAGS.has(flag as QualityFlag));
}

function assertKnownFxQualityFlags(flags: readonly string[]): void {
  const unknown = flags.filter((flag) => !KNOWN_FX_QUALITY_FLAGS.has(flag));
  if (unknown.length > 0) {
    throw new AdapterValidationError(
      `Production FX rate contains unsupported quality flags: ${unknown.join(", ")}.`,
    );
  }
}

function currency(value: string): Currency {
  if (value === "CAD" || value === "USD" || value === "EUR") return value;
  throw new Error(`Unsupported dashboard currency: ${value}.`);
}

function rawPriceFromSettlement(
  settlement: DailySettlement,
  instrument: Instrument,
): RawPrice {
  const quote = instrument.quoteUnit;
  const unit = quote.endsWith("per-bushel")
    ? "bushel"
    : quote.endsWith("per-pound")
      ? "pound"
      : "metric-tonne";
  const scale = quote.startsWith("us-cents-") ? "cents" : "units";
  return {
    stage: "raw",
    instrumentId: instrument.id as InstrumentId,
    date: settlement.exchangeBusinessDate as ISODate,
    value: settlement.rawPrice,
    currency: currency(instrument.quoteCurrency),
    unit,
    scale,
  };
}

function contractRef(contract: FuturesContract): ActualContractPair["legA"] {
  return {
    instrumentId: contract.instrumentId as InstrumentId,
    monthCode: contract.monthCode as FuturesMonthCode,
    year: contract.deliveryYear,
  };
}

function episodeId(pair: ActualContractPair): string {
  return `${pair.pairId}:${formatContract(pair.legA)}/${formatContract(pair.legB)}`;
}

function selectedMetric(controls: DashboardAnalysisControls, primary: DisplayMetric): DisplayMetric {
  return controls.metric === "primary" ? primary : controls.metric;
}

function catalogPrimaryMetric(value: string): DisplayMetric | null {
  if (value === "beta-adjusted-log-residual") return "beta-residual";
  if (value === "raw-spread" || value === "ratio" || value === "log-ratio") return value;
  return null;
}

function latestAvailable<T>(result: MetricResult<T>): T | null {
  return result.status === "available" ? result.value : null;
}

function normalizedMultiplier(instrument: Instrument): number {
  if (instrument.quoteUnit.endsWith("per-bushel")) {
    return instrument.contractMultiplier / 36.7437;
  }
  if (instrument.quoteUnit.endsWith("per-pound")) {
    return instrument.contractMultiplier / 2204.6226218;
  }
  return instrument.contractMultiplier;
}

function signalForDirection(
  signal: number | null,
  direction: DashboardAnalysisControls["direction"],
): number | null {
  if (signal === null) return null;
  // Direction constrains entries only. Clamping to the neutral boundary keeps
  // an existing opposite-side position able to execute its mean-reversion exit.
  if (direction === "leg-a-rich" && signal < 0) return 0;
  if (direction === "leg-a-cheap" && signal > 0) return 0;
  return signal;
}

export class DashboardApplicationService {
  private readonly adapter: MarketDataAdapter;
  private readonly catalog: ApplicationCatalog;
  private readonly source: DashboardApplicationServiceContext["source"];

  constructor(context: DashboardApplicationServiceContext) {
    this.adapter = context.adapter;
    this.catalog = context.catalog;
    this.source = context.source;
  }

  async analyze(request: DashboardAnalysisRequest): Promise<DashboardAnalysisResult> {
    return this.analyzeWithScope(request, "formal-walk-forward");
  }

  async analyzeCurrentKnowledge(
    request: DashboardAnalysisRequest,
  ): Promise<DashboardCurrentKnowledgeResult> {
    return this.analyzeWithScope(request, "current-knowledge");
  }

  async loadPairCatalog(asOf: ISODate): Promise<readonly DashboardPairCatalogEntry[]> {
    const instrumentIds = [...new Set(PAIR_PRESET_LIST.flatMap((preset) => [preset.legA, preset.legB]))];
    const contracts = await this.adapter.listContracts({ instrumentIds, enabledOnly: true });
    if (this.source.mode === "production") {
      const allowed = new Set(instrumentIds);
      const outOfScope = contracts.find((contract) => !contract.enabled || !allowed.has(contract.instrumentId as InstrumentId));
      if (outOfScope) {
        throw new AdapterValidationError(
          `Production contract ${outOfScope.id} is disabled or outside the dashboard catalog scope.`,
        );
      }
    }

    return PAIR_PRESET_LIST.map((preset) => {
      const instruments = this.instrumentPair(preset.legA, preset.legB);
      const pairLabels = pairPresentation(preset.id);
      const manualLegAChoices = contracts
        .filter((contract) =>
          contract.instrumentId === preset.legA &&
          enabledMonthsForLeg(preset, "legA").includes(contract.monthCode as FuturesMonthCode),
        )
        .map((contract) => contractChoice(contract, instruments[0]));
      const manualLegBChoices = contracts
        .filter((contract) =>
          contract.instrumentId === preset.legB &&
          enabledMonthsForLeg(preset, "legB").includes(contract.monthCode as FuturesMonthCode),
        )
        .map((contract) => contractChoice(contract, instruments[1]));
      const auto = resolveAutoMapping(preset.id, asOf);
      const legAContract = contracts.find((contract) =>
        contract.instrumentId === auto.legA.instrumentId &&
        contract.monthCode === auto.legA.monthCode &&
        contract.deliveryYear === auto.legA.year,
      );
      const legBContract = contracts.find((contract) =>
        contract.instrumentId === auto.legB.instrumentId &&
        contract.monthCode === auto.legB.monthCode &&
        contract.deliveryYear === auto.legB.year,
      );
      const legA = manualLegAChoices.find((choice) => choice.dataContractId === legAContract?.id);
      const legB = manualLegBChoices.find((choice) => choice.dataContractId === legBContract?.id);
      return {
        id: preset.id,
        shortLabel: pairLabels.short,
        label: preset.label,
        description: pairLabels.description,
        autoContracts: legA && legB ? { legA, legB } : null,
        manualLegAChoices,
        manualLegBChoices,
      };
    });
  }

  private analyzeWithScope(
    request: DashboardAnalysisRequest,
    scope: "formal-walk-forward",
  ): Promise<DashboardAnalysisResult>;
  private analyzeWithScope(
    request: DashboardAnalysisRequest,
    scope: "current-knowledge",
  ): Promise<DashboardCurrentKnowledgeResult>;
  private async analyzeWithScope(
    request: DashboardAnalysisRequest,
    scope: AnalysisScope,
  ): Promise<DashboardAnalysisResult | DashboardCurrentKnowledgeResult> {
    validateControls(request.controls);
    const coverage = await this.adapter.getCoverage();
    if (
      coverage.sourceId !== this.source.sourceId ||
      (this.source.mode === "production" && isSyntheticSourceId(coverage.sourceId))
    ) {
      throw new AdapterValidationError(
        `Coverage source ${coverage.sourceId} does not match configured ${this.source.mode} source ${this.source.sourceId}.`,
      );
    }
    const config = this.analysisConfig();
    const provenance = this.provenance(coverage, request.asOf, config, scope);
    if (scope === "formal-walk-forward" && coverage.provenance?.formalWalkForwardEligible === false) {
      return this.unavailable(
        request,
        provenance,
        "historical-point-in-time-unavailable",
        coverage.provenance.detail,
      );
    }
    if (coverage.qualityStatus === "unavailable") {
      return this.unavailable(
        request,
        provenance,
        "data-coverage-unavailable",
        "The configured market-data source declares its coverage unavailable; analysis fails closed.",
      );
    }
    const preset = getPairPreset(request.pairPresetId);
    const dataPreset = this.catalog.pairPresets.find((candidate) => candidate.id === request.pairPresetId);
    if (
      !dataPreset ||
      dataPreset.legAInstrumentId !== preset.legA ||
      dataPreset.legBInstrumentId !== preset.legB ||
      catalogPrimaryMetric(dataPreset.primaryDisplayMetric) !== preset.primaryStatisticalMetric
    ) {
      return this.unavailable(
        request,
        provenance,
        "contract-mismatch",
        "The validated data catalog does not match the executable pair preset.",
      );
    }
    const instruments = this.instrumentPair(preset.legA, preset.legB);
    const contracts = await this.adapter.listContracts({
      instrumentIds: [preset.legA, preset.legB],
      enabledOnly: true,
    });
    if (this.source.mode === "production") {
      const outOfScope = contracts.find((contract) =>
        !contract.enabled ||
        (contract.instrumentId !== preset.legA && contract.instrumentId !== preset.legB),
      );
      if (outOfScope) {
        throw new AdapterValidationError(
          `Production contract ${outOfScope.id} is disabled or outside the requested pair instrument scope.`,
        );
      }
    }
    const resolved = this.resolveContracts(request, contracts);
    if (resolved.status === "not-available") {
      return this.unavailable(request, provenance, resolved.reason, resolved.detail);
    }
    const { pair, legAContract, legBContract } = resolved;
    if (legAContract.deliveryYear !== legBContract.deliveryYear) {
      return this.unavailable(
        request,
        provenance,
        "unsupported-manual-template",
        "Seasonal and walk-forward analysis requires both manually selected legs to share a contract year.",
      );
    }

    const currentEpisode = await this.loadEpisode(
      pair,
      legAContract,
      legBContract,
      instruments,
      request.asOf,
      request.controls,
      scope,
    );
    if (!currentEpisode || currentEpisode.points.length < config.minimumSample) {
      return this.unavailable(
        request,
        provenance,
        currentEpisode ? "insufficient-eligible-observations" : "contract-data-unavailable",
        currentEpisode
          ? `Only ${currentEpisode.points.length} eligible observations remain; ${config.minimumSample} are required.`
          : "The selected actual contracts have no same-date, normalized settlements at the requested as-of cutoff.",
      );
    }

    const metric = selectedMetric(request.controls, preset.primaryStatisticalMetric);
    const currentAnalysis = this.analyzeEpisode(currentEpisode, request, config, metric);
    if (currentAnalysis.metricSeries.status === "not-available") {
      return this.unavailable(
        request,
        provenance,
        "insufficient-eligible-observations",
        currentAnalysis.metricSeries.detail,
      );
    }

    const history = await this.loadComparableHistory(
      request,
      contracts,
      currentEpisode,
      instruments,
      config,
      metric,
      scope,
    );
    const currentSeasonal = this.seasonalEpisode(currentEpisode, currentAnalysis);
    const historicalSeasonal = history.map((item) => item.seasonal);
    const seasonality = analyzeSeasonalPanel(currentSeasonal, historicalSeasonal, {
      minimumHistoricalYears: 5,
      halfWindowBusinessObservations: 10,
      location: "mean",
      dispersion: "winsorized-standard-deviation",
    });
    const currentSeasonalZ = seasonality.seasonalZ;

    const stationarity = this.stationarity(currentEpisode, currentAnalysis, request.controls.window);
    const windowSensitivity = analyzeWindowSensitivity(currentEpisode.points, {
      pairPresetId: request.pairPresetId,
      metric,
      windows: UI_WINDOWS,
    });

    const analyzedEpisodes = this.attachWalkForwardSeasonalZ([
      ...history,
      {
        episode: currentEpisode,
        analysis: currentAnalysis,
        seasonal: currentSeasonal,
        seasonalZ: currentSeasonalZ,
      },
    ]);
    const walkForward = this.backtest(
      analyzedEpisodes,
      request.controls,
      config,
      instruments,
    );
    const matifDecomposition = request.pairPresetId === "canola-matif-rapeseed"
      ? (() => {
          const canola = preset.canolaLeg === "legA" ? pair.legA : pair.legB;
          const matif = preset.canolaLeg === "legA" ? pair.legB : pair.legA;
          const latest = currentEpisode.points.at(-1)!;
          const canolaValue = preset.canolaLeg === "legA" ? latest.legA : latest.legB;
          const matifValue = preset.canolaLeg === "legA" ? latest.legB : latest.legA;
          const template = `${canola.monthCode}/${matif.monthCode}`;
          switch (template) {
            case "X/X":
            case "K/K":
              return decomposeMatifPair({
                template,
                canola: canolaValue,
                matif: matifValue,
              });
            case "F/G":
              return decomposeMatifPair({ template });
            case "H/K":
              return notAvailable(
                "unsupported-template",
                "H/K decomposition requires a same-year Canola K anchor; the selected actual-contract episode contains only H/K executable legs.",
                0,
              );
            case "N/Q":
              return notAvailable(
                "unsupported-template",
                "N/Q decomposition requires same-year Canola X and MATIF X anchors; the selected actual-contract episode contains only N/Q executable legs.",
                0,
              );
            default:
              return notAvailable(
                "unsupported-template",
                `MATIF decomposition does not define the actual ${template} contract template.`,
                0,
              );
          }
        })()
      : null;
    const matifFxAttribution = request.pairPresetId === "canola-matif-rapeseed"
      ? currentEpisode.matifFxInput
        ? available(
            attributeFxAdjustedPriceChange(currentEpisode.matifFxInput),
            2,
            "Exact midpoint-Shapley attribution on two same-date actual MATIF settlements and EUR/CAD fixes",
          )
        : notAvailable(
            "insufficient-eligible-observations",
            "Two same-date MATIF raw settlements and EUR/CAD fixes are required for FX attribution.",
            currentEpisode.points.length,
          )
      : null;
    const maturityGap = Math.abs(currentEpisode.legAContract.deliveryMonth - currentEpisode.legBContract.deliveryMonth);
    const matifMaturity = request.pairPresetId === "canola-matif-rapeseed"
      ? {
          template: `${pair.legA.monthCode}/${pair.legB.monthCode}`,
          gapMonths: maturityGap,
          quality: maturityGap === 0 ? "matched" as const : "maturity-gap" as const,
          detail: maturityGap === 0
            ? "Matched delivery month; no maturity-gap attribution is required."
            : `${maturityGap}-month delivery mismatch; curve anchors are required for exact decomposition.`,
        }
      : null;

    const diagnosticBase = {
      analysisConfig: {
        id: config.id,
        version: config.version,
      },
      actualContracts: {
        legAContractId: legAContract.id,
        legBContractId: legBContract.id,
        legAContract: formatContract(pair.legA),
        legBContract: formatContract(pair.legB),
      },
      selectedMetric: metric,
      primaryStatisticalMetric: preset.primaryStatisticalMetric,
      currentEpisode: {
        episodeId: episodeId(pair),
        dates: currentEpisode.points.map((point) => point.date),
        eligibleObservations: currentEpisode.points.length,
        excludedObservations: currentEpisode.excludedCount,
        latestDate: currentEpisode.points.at(-1)!.date,
      },
      currentLocalZ: currentAnalysis.localZ.at(-1)?.value ?? null,
      currentSeasonalZ: currentSeasonalZ.at(-1)?.value ?? null,
      adfResidual: stationarity.adf,
      engleGranger: stationarity.eg,
      halfLife: stationarity.halfLife,
      seasonality,
      windowSensitivity,
      episodeContractYears: Object.fromEntries(analyzedEpisodes.map((item) => [
        episodeId(item.episode.pair),
        item.seasonal.contractYear,
      ])),
      matifDecomposition,
      matifFxAttribution,
      matifMaturity,
    };
    const descriptiveAnalytics = [
      "analyzeActualEpisode",
      "analyzeSeasonalPanel",
      "analyzeWindowSensitivity",
      "augmentedDickeyFuller",
      "engleGrangerCointegration",
      "estimateAr1HalfLife",
      ...(matifDecomposition ? ["decomposeMatifPair"] : []),
      ...(matifFxAttribution ? ["attributeFxAdjustedPriceChange"] : []),
    ];

    if (scope === "current-knowledge") {
      const diagnostics: DashboardCurrentKnowledgeDiagnostics = {
        ...diagnosticBase,
        analysisScope: "latest-release-research",
        formalWalkForward: CURRENT_KNOWLEDGE_FORMAL_UNAVAILABLE,
        backtest: walkForward.backtest,
        forwardStudy: this.forwardStudy(walkForward.forwardPoints, request.controls, metric, config),
        invokedAnalytics: [
          ...descriptiveAnalytics,
          "runActualContractBacktest",
          "studyForwardReversion",
        ],
      };
      return {
        status: "current-knowledge-ready",
        fixture: this.projectFixture({
          request,
          preset,
          contracts,
          currentEpisode,
          currentAnalysis,
          analyzedEpisodes,
          diagnostics,
          config,
          instruments,
          coverage,
        }),
        diagnostics,
        provenance,
      };
    }

    const diagnostics: DashboardAnalysisDiagnostics = {
      ...diagnosticBase,
      backtest: walkForward.backtest,
      forwardStudy: this.forwardStudy(walkForward.forwardPoints, request.controls, metric, config),
      invokedAnalytics: [
        ...descriptiveAnalytics,
        "runActualContractBacktest",
        "studyForwardReversion",
      ],
    };

    return {
      status: "ready",
      fixture: this.projectFixture({
        request,
        preset,
        contracts,
        currentEpisode,
        currentAnalysis,
        analyzedEpisodes,
        diagnostics,
        config,
        instruments,
        coverage,
      }),
      diagnostics,
      provenance,
    };
  }

  private provenance(
    coverage: DataCoverage,
    requestAsOf: ISODate,
    config: AnalysisConfig,
    scope: AnalysisScope,
  ): ApplicationDataProvenance {
    return {
      ...this.source,
      requestAsOf,
      inputCutoffTimestamp: scope === "formal-walk-forward"
        ? asOfTimestamp(requestAsOf)
        : null,
      knowledgeScope: scope === "formal-walk-forward" ? "point-in-time" : "current-knowledge",
      coverage,
      analysisConfigId: config.id,
      analysisConfigVersion: config.version,
    };
  }

  private unavailable(
    request: DashboardAnalysisRequest,
    provenance: ApplicationDataProvenance,
    reason: Extract<DashboardAnalysisResult, { status: "not-available" }>["reason"],
    detail: string,
  ): Extract<DashboardAnalysisResult, { status: "not-available" }> {
    return { status: "not-available", pairPresetId: request.pairPresetId, reason, detail, provenance };
  }

  private instrumentPair(legAId: InstrumentId, legBId: InstrumentId): readonly [Instrument, Instrument] {
    const legA = this.catalog.instruments.find((instrument) => instrument.id === legAId);
    const legB = this.catalog.instruments.find((instrument) => instrument.id === legBId);
    if (!legA || !legB) throw new Error("Validated catalog is missing pair instrument metadata.");
    return [legA, legB];
  }

  private analysisConfig(): AnalysisConfig {
    const ordered = [...this.catalog.analysisConfigs].sort((left, right) =>
      left.id.localeCompare(right.id) ||
      right.version.localeCompare(left.version, undefined, { numeric: true, sensitivity: "base" }),
    );
    const config = ordered.find((candidate) => candidate.id === "dashboard-default") ?? ordered[0];
    if (!config) throw new Error("Validated catalog has no analysis configuration.");
    return config;
  }

  private resolveContracts(
    request: DashboardAnalysisRequest,
    contracts: readonly FuturesContract[],
  ):
    | {
        readonly status: "available";
        readonly pair: ActualContractPair;
        readonly legAContract: FuturesContract;
        readonly legBContract: FuturesContract;
      }
    | {
        readonly status: "not-available";
        readonly reason: "contract-not-found" | "contract-mismatch";
        readonly detail: string;
      } {
    const preset = getPairPreset(request.pairPresetId);
    if (request.mapping.mode === "auto") {
      const pair = resolveAutoMapping(request.pairPresetId, request.asOf);
      const legAContract = contracts.find((candidate) =>
        candidate.enabled &&
        candidate.instrumentId === pair.legA.instrumentId &&
        candidate.monthCode === pair.legA.monthCode &&
        candidate.deliveryYear === pair.legA.year,
      );
      const legBContract = contracts.find((candidate) =>
        candidate.enabled &&
        candidate.instrumentId === pair.legB.instrumentId &&
        candidate.monthCode === pair.legB.monthCode &&
        candidate.deliveryYear === pair.legB.year,
      );
      if (!legAContract || !legBContract) {
        return {
          status: "not-available",
          reason: "contract-not-found",
          detail: `Auto mapping resolved ${formatContract(pair.legA)} / ${formatContract(pair.legB)}, but one or both actual contracts are absent.`,
        };
      }
      return { status: "available", pair, legAContract, legBContract };
    }

    const { legAContractId, legBContractId } = request.mapping;
    const legAContract = contracts.find((candidate) => candidate.enabled && candidate.id === legAContractId);
    const legBContract = contracts.find((candidate) => candidate.enabled && candidate.id === legBContractId);
    if (!legAContract || !legBContract) {
      return {
        status: "not-available",
        reason: "contract-not-found",
        detail: "At least one manually selected contract is not present in the validated catalog.",
      };
    }
    if (
      legAContract.instrumentId !== preset.legA ||
      legBContract.instrumentId !== preset.legB ||
      !enabledMonthsForLeg(preset, "legA").includes(legAContract.monthCode as FuturesMonthCode) ||
      !enabledMonthsForLeg(preset, "legB").includes(legBContract.monthCode as FuturesMonthCode)
    ) {
      return {
        status: "not-available",
        reason: "contract-mismatch",
        detail: "Manual contracts must match the selected pair legs and use only the preset's enabled contract months (U is disabled).",
      };
    }
    const pair: ActualContractPair = {
      pairId: request.pairPresetId,
      legA: contractRef(legAContract),
      legB: contractRef(legBContract),
    };
    return { status: "available", pair, legAContract, legBContract };
  }

  private async loadEpisode(
    pair: ActualContractPair,
    legAContract: FuturesContract,
    legBContract: FuturesContract,
    instruments: readonly [Instrument, Instrument],
    asOf: ISODate,
    controls: DashboardAnalysisControls,
    scope: AnalysisScope,
  ): Promise<EpisodeBundle | null> {
    const preset = getPairPreset(pair.pairId);
    const canola = preset.canolaLeg === "legA" ? pair.legA : pair.legB;
    const window = getSeasonalityWindow(
      canola.monthCode as EnabledCanolaMonthCode,
      canola.year,
    );
    const endDate = window.end < asOf ? window.end : asOf;
    if (window.start > endDate) return null;
    const settlements = await this.adapter.getSettlements({
      contractIds: [legAContract.id, legBContract.id],
      startDate: window.start,
      endDate,
      ...(scope === "formal-walk-forward"
        ? { asOfTimestamp: asOfTimestamp(asOf), purpose: "walk-forward" as const }
        : { purpose: "latest" as const }),
    });
    const fxPairs = [...new Set(instruments
      .map((instrument) => instrument.quoteCurrency)
      .filter((quoteCurrency) => quoteCurrency !== "CAD")
      .map((quoteCurrency) => `${quoteCurrency}CAD`))];
    const fxRows = fxPairs.length > 0
      ? await this.adapter.getFxRates({
          pairs: fxPairs,
          startDate: window.start,
          endDate,
          ...(scope === "formal-walk-forward"
            ? { asOfTimestamp: asOfTimestamp(asOf), purpose: "walk-forward" as const }
            : { purpose: "latest" as const }),
        })
      : [];
    const invalidSettlementSource = settlements.find((row) =>
      row.source !== this.source.sourceId ||
      (this.source.mode === "production" && isSyntheticSourceId(row.source)),
    );
    const invalidFxSource = fxRows.find((row) =>
      row.source !== this.source.sourceId ||
      (this.source.mode === "production" && isSyntheticSourceId(row.source)),
    );
    if (invalidSettlementSource || invalidFxSource) {
      throw new AdapterValidationError(
        `Market-data record source does not match configured ${this.source.mode} source ${this.source.sourceId}.`,
      );
    }
    if (this.source.mode === "production") {
      for (const settlement of settlements) {
        recognizedQualityFlags(settlement.qualityFlags, true);
      }
      for (const rate of fxRows) assertKnownFxQualityFlags(rate.qualityFlags);
    }
    // A walk-forward query may expose only the latest revision available at
    // the episode cutoff. If that revision was ingested after the row's own
    // business date, the application cannot prove the older value and drops it.
    const admittedSettlements = scope === "formal-walk-forward"
      ? settlements.filter((row) =>
          Date.parse(row.ingestedAt) <= Date.parse(asOfTimestamp(row.exchangeBusinessDate as ISODate)),
        )
      : settlements;
    const admittedFxRows = scope === "formal-walk-forward"
      ? fxRows.filter((row) =>
          Date.parse(row.ingestedAt) <= Date.parse(asOfTimestamp(row.date as ISODate)),
        )
      : fxRows;
    const fx = admittedFxRows.map((rate) => ({
      date: rate.date as ISODate,
      base: rate.pair.slice(0, 3) as Currency,
      quote: rate.pair.slice(3) as Currency,
      rate: rate.rate,
      valid: !rate.qualityFlags.includes("invalid-fx") &&
        !(scope === "formal-walk-forward" &&
          controls.quality.excludeSourceWarnings &&
          rate.qualityFlags.includes("source-warning")),
    }));

    const normalizeLeg = (contract: FuturesContract, instrument: Instrument) =>
      flagConsecutiveStalePrices(
        admittedSettlements
          .filter((settlement) => settlement.contractId === contract.id)
          .map((settlement) => {
            const normalized = normalizeToCadPerMetricTonne(
              rawPriceFromSettlement(settlement, instrument),
              fx,
            );
            if (normalized.status === "unavailable") return null;
            return {
              date: settlement.exchangeBusinessDate as ISODate,
              value: normalized.value.value,
              settlementTimestamp: settlement.settlementTimestamp,
              qualityFlags: recognizedQualityFlags(
                settlement.qualityFlags,
                this.source.mode === "production",
              ),
              volume: settlement.volume,
              openInterest: settlement.openInterest,
            };
          })
          .filter((value): value is NonNullable<typeof value> => value !== null),
        3,
      );
    const legA = normalizeLeg(legAContract, instruments[0]);
    const legB = normalizeLeg(legBContract, instruments[1]);
    const aligned = alignSameDateIntersection(legA, legB, {
      excludeLowLiquidity: controls.quality.excludeLowLiquidity,
      excludeSourceWarnings: scope === "formal-walk-forward" && controls.quality.excludeSourceWarnings,
    });
    if (aligned.eligible.length === 0) return null;
    const matifLeg = instruments[0].id === "MATIF_RAPESEED" ? "legA" : instruments[1].id === "MATIF_RAPESEED" ? "legB" : null;
    const matifContract = matifLeg === "legA" ? legAContract : matifLeg === "legB" ? legBContract : null;
    const matifFxInput = matifContract && aligned.eligible.length >= 2
      ? (() => {
          const previousDate = aligned.eligible.at(-2)!.date;
          const currentDate = aligned.eligible.at(-1)!.date;
          const previousSettlement = admittedSettlements.find((row) =>
            row.contractId === matifContract.id && row.exchangeBusinessDate === previousDate,
          );
          const currentSettlement = admittedSettlements.find((row) =>
            row.contractId === matifContract.id && row.exchangeBusinessDate === currentDate,
          );
          const previousFx = admittedFxRows.find((row) =>
            row.pair === "EURCAD" && row.date === previousDate &&
            !row.qualityFlags.includes("invalid-fx") &&
            !(scope === "formal-walk-forward" && controls.quality.excludeSourceWarnings && row.qualityFlags.includes("source-warning")),
          );
          const currentFx = admittedFxRows.find((row) =>
            row.pair === "EURCAD" && row.date === currentDate &&
            !row.qualityFlags.includes("invalid-fx") &&
            !(scope === "formal-walk-forward" && controls.quality.excludeSourceWarnings && row.qualityFlags.includes("source-warning")),
          );
          return previousSettlement && currentSettlement && previousFx && currentFx
            ? {
                previousLocalPrice: previousSettlement.rawPrice,
                currentLocalPrice: currentSettlement.rawPrice,
                previousFx: previousFx.rate,
                currentFx: currentFx.rate,
              }
            : undefined;
        })()
      : undefined;
    return {
      pair,
      legAContract,
      legBContract,
      points: aligned.eligible.map((observation) => ({
        date: observation.date,
        legA: observation.legA.value,
        legB: observation.legB.value,
        legAContract: formatContract(pair.legA),
        legBContract: formatContract(pair.legB),
      })),
      excludedCount: aligned.excluded.length +
        (settlements.length - admittedSettlements.length) +
        (fxRows.length - admittedFxRows.length) +
        admittedFxRows.filter((row) =>
          row.qualityFlags.includes("invalid-fx") ||
          (scope === "formal-walk-forward" && controls.quality.excludeSourceWarnings && row.qualityFlags.includes("source-warning")),
        ).length,
      dateStart: aligned.eligible[0]!.date,
      dateEnd: aligned.eligible.at(-1)!.date,
      ...(matifFxInput ? { matifFxInput } : {}),
    };
  }

  private analyzeEpisode(
    episode: EpisodeBundle,
    request: DashboardAnalysisRequest,
    config: AnalysisConfig,
    metric: DisplayMetric,
  ): ActualEpisodeAnalysis {
    return analyzeActualEpisode({
      kind: "actual-episode",
      pair: episode.pair,
      episodeId: episodeId(episode.pair),
      source: "actual-settlements",
      backAdjusted: false,
      points: episode.points,
    }, {
      pairPresetId: request.pairPresetId,
      metric,
      minimumSample: config.minimumSample,
      zWindow: request.controls.window,
      volatilityWindow: Math.min(20, request.controls.window),
    });
  }

  private seasonalEpisode(
    episode: EpisodeBundle,
    analysis: ActualEpisodeAnalysis,
  ): SeasonalValueEpisode {
    if (analysis.metricSeries.status === "not-available") {
      throw new Error("A seasonal episode requires an available selected metric.");
    }
    const preset = getPairPreset(episode.pair.pairId);
    const canola = preset.canolaLeg === "legA" ? episode.pair.legA : episode.pair.legB;
    return {
      pairPresetId: episode.pair.pairId,
      templateId: `${episode.pair.legA.monthCode}/${episode.pair.legB.monthCode}`,
      contractYear: canola.year,
      actualContracts: episode.pair,
      points: analysis.metricSeries.value.points,
    };
  }

  private async loadComparableHistory(
    request: DashboardAnalysisRequest,
    contracts: readonly FuturesContract[],
    current: EpisodeBundle,
    instruments: readonly [Instrument, Instrument],
    config: AnalysisConfig,
    metric: DisplayMetric,
    scope: AnalysisScope,
  ): Promise<EpisodeAnalysisBundle[]> {
    const preset = getPairPreset(current.pair.pairId);
    const currentYear = (preset.canolaLeg === "legA" ? current.pair.legA : current.pair.legB).year;
    const results: EpisodeAnalysisBundle[] = [];
    for (let year = currentYear - 5; year < currentYear; year += 1) {
      const legAContract = contracts.find((candidate) =>
        candidate.instrumentId === current.pair.legA.instrumentId &&
        candidate.monthCode === current.pair.legA.monthCode &&
        candidate.deliveryYear === year,
      );
      const legBContract = contracts.find((candidate) =>
        candidate.instrumentId === current.pair.legB.instrumentId &&
        candidate.monthCode === current.pair.legB.monthCode &&
        candidate.deliveryYear === year,
      );
      if (!legAContract || !legBContract) continue;
      const pair: ActualContractPair = {
        pairId: current.pair.pairId,
        legA: contractRef(legAContract),
        legB: contractRef(legBContract),
      };
      const historicalCanola = preset.canolaLeg === "legA" ? pair.legA : pair.legB;
      const historicalWindow = getSeasonalityWindow(
        historicalCanola.monthCode as EnabledCanolaMonthCode,
        historicalCanola.year,
      );
      const episodeCutoff = historicalWindow.end < request.asOf
        ? historicalWindow.end
        : request.asOf;
      const episode = await this.loadEpisode(
        pair,
        legAContract,
        legBContract,
        instruments,
        episodeCutoff,
        request.controls,
        scope,
      );
      if (!episode || episode.points.length < config.minimumSample) continue;
      const analysis = this.analyzeEpisode(episode, request, config, metric);
      if (analysis.metricSeries.status === "not-available") continue;
      results.push({
        episode,
        analysis,
        seasonal: this.seasonalEpisode(episode, analysis),
        seasonalZ: [],
      });
    }
    return results;
  }

  private attachWalkForwardSeasonalZ(
    input: readonly EpisodeAnalysisBundle[],
  ): EpisodeAnalysisBundle[] {
    const sorted = [...input].sort(
      (left, right) => left.seasonal.contractYear - right.seasonal.contractYear,
    );
    return sorted.map((item, index) => ({
      ...item,
      seasonalZ: calculateSeasonalZScores(item.seasonal, sorted.slice(0, index).map((prior) => prior.seasonal), {
        minimumHistoricalYears: 5,
        halfWindowBusinessObservations: 10,
        location: "mean",
        dispersion: "winsorized-standard-deviation",
      }),
    }));
  }

  private stationarity(
    episode: EpisodeBundle,
    analysis: ActualEpisodeAnalysis,
    analysisWindow: number,
  ) {
    const eg = engleGrangerCointegration(
      episode.points.map((point) => point.legA),
      episode.points.map((point) => point.legB),
      { minimumSample: 30, lags: 1 },
    );
    const adf = analysis.betaResidual.status === "available"
      ? augmentedDickeyFuller(analysis.betaResidual.value.residuals.map((point) => point.value), {
          minimumSample: 30,
          lags: 1,
        })
      : analysis.betaResidual;
    const stationaritySupported = eg.status === "available" &&
      (eg.value.rejectAt === "1%" || eg.value.rejectAt === "5%") &&
      adf.status === "available" &&
      (adf.value.rejectAt === "1%" || adf.value.rejectAt === "5%");
    const halfLife = analysis.betaResidual.status === "available"
      ? estimateAr1HalfLife(analysis.betaResidual.value.residuals.map((point) => point.value), {
          minimumSample: 30,
          analysisWindow,
          stationaritySupported,
        })
      : analysis.betaResidual;
    return { eg, adf, halfLife };
  }

  private backtest(
    episodes: readonly EpisodeAnalysisBundle[],
    controls: DashboardAnalysisControls,
    config: AnalysisConfig,
    instruments: readonly [Instrument, Instrument],
  ) {
    const sorted = [...episodes].sort(
      (left, right) => left.seasonal.contractYear - right.seasonal.contractYear,
    );
    const walkForwardSeasonals: SeasonalValueEpisode[] = [];
    const forwardPoints: ForwardReversionPoint[] = [];
    const backtestEpisodes: ActualBacktestEpisode[] = sorted.map((item) => {
      const prefixByDate = new Map<ISODate, {
        readonly metricValue: number | null;
        readonly localZ: number | null;
        readonly beta: number | null;
      }>();
      for (let endIndex = 0; endIndex < item.episode.points.length; endIndex += 1) {
        const points = item.episode.points.slice(0, endIndex + 1);
        const date = points.at(-1)!.date;
        const prefix = analyzeActualEpisode({
          kind: "actual-episode",
          pair: item.episode.pair,
          episodeId: `${episodeId(item.episode.pair)}:through:${date}`,
          source: "actual-settlements",
          backAdjusted: false,
          points,
        }, {
          pairPresetId: item.episode.pair.pairId,
          metric: item.analysis.selectedMetric,
          minimumSample: config.minimumSample,
          zWindow: controls.window,
          volatilityWindow: Math.min(20, controls.window),
        });
        prefixByDate.set(date, {
          metricValue: prefix.metricSeries.status === "available"
            ? prefix.metricSeries.value.points.at(-1)?.value ?? null
            : null,
          localZ: prefix.localZ.at(-1)?.value ?? null,
          beta: prefix.betaResidual.status === "available" ? prefix.betaResidual.value.beta : null,
        });
      }
      const walkForwardSeasonal: SeasonalValueEpisode = {
        ...item.seasonal,
        points: item.episode.points.flatMap((point) => {
          const value = prefixByDate.get(point.date)?.metricValue ?? null;
          return value === null ? [] : [{ date: point.date, value }];
        }),
      };
      const seasonalByDate = new Map(calculateSeasonalZScores(
        walkForwardSeasonal,
        walkForwardSeasonals,
        {
          minimumHistoricalYears: 5,
          halfWindowBusinessObservations: 10,
          location: "mean",
          dispersion: "winsorized-standard-deviation",
        },
      ).map((point) => [point.date, point]));
      walkForwardSeasonals.push(walkForwardSeasonal);
      for (const point of item.episode.points) {
        const metricValue = prefixByDate.get(point.date)?.metricValue ?? null;
        const seasonal = seasonalByDate.get(point.date);
        if (metricValue === null || !seasonal || seasonal.value === null) continue;
        forwardPoints.push({
          date: point.date,
          episodeId: episodeId(item.episode.pair),
          metricValue,
          seasonalZ: seasonal.value,
          eligible: true,
          contractYear: item.seasonal.contractYear,
          pairId: item.seasonal.pairPresetId,
        });
      }
      return {
        kind: "actual-episode",
        source: "actual-settlements",
        backAdjusted: false,
        episodeId: episodeId(item.episode.pair),
        contractYear: item.seasonal.contractYear,
        legAContract: formatContract(item.episode.pair.legA),
        legBContract: formatContract(item.episode.pair.legB),
        observations: item.episode.points.map((point) => {
          const prefix = prefixByDate.get(point.date);
          const local = prefix?.localZ ?? null;
          const seasonal = seasonalByDate.get(point.date)?.value ?? null;
          let rawSignal = controls.signalType === "local-z"
            ? local
            : controls.signalType === "seasonal-z"
              ? seasonal
              : local !== null && seasonal !== null
                ? (local + seasonal) / 2
                : null;
          if (controls.sizing === "beta-neutral" && (prefix?.beta == null || prefix.beta <= 0)) {
            rawSignal = null;
          }
          return {
            date: point.date,
            legAPrice: point.legA,
            legBPrice: point.legB,
            signal: signalForDirection(rawSignal, controls.direction),
            signalAsOf: point.date,
            ...(prefix?.beta !== null && prefix?.beta !== undefined
              ? { beta: prefix.beta }
              : {}),
            eligible: true,
            isForwardFilled: false,
            baselineContractYears: seasonalByDate.get(point.date)?.baselineYears ?? [],
          };
        }),
      };
    });
    const costs = config.costAssumptions;
    const sizing = controls.sizing === "fixed-ratio"
      ? { type: "fixed-ratio" as const, legAContracts: 25, legBContracts: 25, rounding: "nearest" as const }
      : controls.sizing === "notional-neutral"
        ? { type: "notional-neutral" as const, legAContracts: 25, rounding: "nearest" as const }
        : { type: "beta-neutral" as const, legAContracts: 25, rounding: "nearest" as const };
    const legCosts = {
      bidAskHalfSpread: costs.bidAskHalfSpread ?? 0,
      commission: costs.commission ?? 0,
      exchangeFees: costs.exchangeFees ?? 0,
      slippage: costs.slippage ?? 0,
      fxConversion: costs.fxConversion ?? 0,
      rollExecution: costs.rollExecution ?? 0,
    };
    const legCostsFor = (instrument: Instrument) => ({
      ...legCosts,
      fxConversion: instrument.quoteCurrency === "CAD" ? 0 : legCosts.fxConversion,
    });
    const result = runActualContractBacktest(backtestEpisodes, {
      entryZ: controls.entryZ,
      exitZ: controls.exitZ,
      maximumHoldingObservations: 40,
      sizing,
      legAMultiplier: normalizedMultiplier(instruments[0]),
      legBMultiplier: normalizedMultiplier(instruments[1]),
      legACurrency: "CAD",
      legBCurrency: "CAD",
      commonCurrency: "CAD",
      legACosts: legCostsFor(instruments[0]),
      legBCosts: legCostsFor(instruments[1]),
      initialCapital: 1_000_000,
    });
    return {
      backtest: {
        ...result,
        events: result.events.map((event) => ({
          ...event,
          modelParametersAsOf: event.signalAsOf,
        })),
      },
      forwardPoints,
    };
  }

  private forwardStudy(
    points: readonly ForwardReversionPoint[],
    controls: DashboardAnalysisControls,
    metric: DisplayMetric,
    config: AnalysisConfig,
  ) {
    const perExecutionCost = Object.values(config.costAssumptions).reduce((sum, value) => sum + value, 0);
    const metricCost = metric === "raw-spread" ? perExecutionCost / 20 : perExecutionCost / 1_000;
    return studyForwardReversion(points, {
      thresholds: [controls.entryZ],
      horizons: [5, 10, 20, 40],
      roundTripCostInMetricUnits: metricCost * 2,
      filter: (point) => controls.direction === "both" ||
        (controls.direction === "leg-a-rich" ? point.seasonalZ > 0 : point.seasonalZ < 0),
    });
  }

  private projectFixture(input: {
    readonly request: DashboardAnalysisRequest;
    readonly preset: ReturnType<typeof getPairPreset>;
    readonly contracts: readonly FuturesContract[];
    readonly currentEpisode: EpisodeBundle;
    readonly currentAnalysis: ActualEpisodeAnalysis;
    readonly analyzedEpisodes: readonly EpisodeAnalysisBundle[];
    readonly diagnostics: ProjectionDiagnostics;
    readonly config: AnalysisConfig;
    readonly instruments: readonly [Instrument, Instrument];
    readonly coverage: DataCoverage;
  }): PairDashboardFixture {
    const {
      request,
      preset,
      contracts,
      currentEpisode,
      currentAnalysis,
      analyzedEpisodes,
      diagnostics,
      config,
      instruments,
      coverage,
    } = input;
    if (currentAnalysis.metricSeries.status === "not-available") {
      throw new Error("Projection requires an available current metric series.");
    }
    const metricPoints = currentAnalysis.metricSeries.value.points;
    const latestMetric = metricPoints.at(-1)!.value;
    const latestLocalZ = diagnostics.currentLocalZ;
    const latestSeasonalZ = diagnostics.currentSeasonalZ;
    const beta = latestAvailable(currentAnalysis.betaResidual)?.beta ?? null;
    const correlation = latestAvailable(currentAnalysis.returnCorrelation)?.correlation ?? null;
    const adfBand = latestAvailable(diagnostics.adfResidual)?.approximateProbabilityBand ?? null;
    const egBand = latestAvailable(diagnostics.engleGranger)?.approximateProbabilityBand ?? null;
    const halfLife = latestAvailable(diagnostics.halfLife)?.halfLife ?? null;
    const quality = coverage.qualityStatus === "low-quality"
      ? "low-quality" as const
      : coverage.qualityStatus === "partial" || currentEpisode.excludedCount > 0
        ? "partial" as const
        : "good" as const;
    const selectedSignal = request.controls.signalType === "local-z"
      ? latestLocalZ
      : request.controls.signalType === "seasonal-z"
        ? latestSeasonalZ
        : latestLocalZ !== null && latestSeasonalZ !== null
          ? (latestLocalZ + latestSeasonalZ) / 2
          : null;
    const pairLabels = pairPresentation(request.pairPresetId);
    const adf = latestAvailable(diagnostics.adfResidual);
    const eg = latestAvailable(diagnostics.engleGranger);
    const adfSupportsStationarity = adf?.rejectAt === "1%" || adf?.rejectAt === "5%";
    const egSupportsStationarity = eg?.rejectAt === "1%" || eg?.rejectAt === "5%";
    const stability = !adf || !eg
      ? "Unknown" as const
      : adfSupportsStationarity && egSupportsStationarity && diagnostics.halfLife.status === "available"
        ? "Stable" as const
        : adfSupportsStationarity !== egSupportsStationarity ||
            (adfSupportsStationarity && egSupportsStationarity)
          ? "Mixed" as const
          : "Unstable" as const;
    const signal = selectedSignal === null
      ? "N/A" as const
      : selectedSignal >= request.controls.entryZ
        ? `${pairLabels.legAName} rich`
        : selectedSignal <= -request.controls.entryZ
          ? `${pairLabels.legAName} cheap`
          : Math.abs(selectedSignal) >= request.controls.entryZ * 0.6
            ? "Watch" as const
            : "Neutral" as const;

    const spreadPoints = projectSpreadPoints(metricPoints, currentAnalysis.localZ, request.controls.window);
    const activeContracts = {
      legA: contractChoice(currentEpisode.legAContract, instruments[0]),
      legB: contractChoice(currentEpisode.legBContract, instruments[1]),
    };
    const seasonality = projectSeasonality(analyzedEpisodes, diagnostics);
    const formalWalkForward = !("analysisScope" in diagnostics);
    const strategy = projectStrategy(diagnostics.backtest);
    const forwardStudy = projectForwardStudy(diagnostics.forwardStudy);
    const windows = projectWindows(diagnostics.windowSensitivity);
    const rolling = this.projectRegime(
      currentEpisode,
      diagnostics,
      request,
      config,
    );
    const leadLag = projectLeadLag(currentEpisode.points);
    const unit = diagnostics.selectedMetric === "raw-spread"
      ? "CAD/metric-tonne"
      : diagnostics.selectedMetric === "ratio"
        ? "ratio"
        : "log units";
    const costDescription = Object.entries(config.costAssumptions)
      .map(([name, value]) => `${name} ${value.toFixed(2)}`)
      .join("; ");
    const decompositionByTemplate = projectMatifDecomposition(diagnostics);
    const metrics: DashboardMetric[] = [
      dashboardMetric("Current value", formatNumber(latestMetric, 4), diagnostics.selectedMetric),
      dashboardMetric("Local Z", latestLocalZ === null ? "N/A" : formatNumber(latestLocalZ, 2), `window=${request.controls.window}`),
      dashboardMetric("Seasonal Z", latestSeasonalZ === null ? "N/A" : formatNumber(latestSeasonalZ, 2), "current contract year excluded from baseline"),
      dashboardMetric("Return correlation", formatNumber(correlation, 2), "daily log returns"),
      dashboardMetric("Beta", formatNumber(beta, 3), "within-episode log-price OLS"),
    ];
    if (diagnostics.matifDecomposition?.status === "available") {
      metrics.push(dashboardMetric(
        "MATIF decomposition",
        formatNumber(diagnostics.matifDecomposition.value.executableSpread, 2),
        `${diagnostics.matifDecomposition.value.template}; exact accounting identity`,
      ));
    }

    return {
      id: request.pairPresetId,
      shortLabel: pairLabels.short,
      label: preset.label,
      description: pairLabels.description,
      autoContracts: activeContracts,
      manualLegAChoices: contracts
        .filter((contract) =>
          contract.instrumentId === preset.legA &&
          enabledMonthsForLeg(preset, "legA").includes(contract.monthCode as FuturesMonthCode),
        )
        .map((contract) => contractChoice(contract, instruments[0])),
      manualLegBChoices: contracts
        .filter((contract) =>
          contract.instrumentId === preset.legB &&
          enabledMonthsForLeg(preset, "legB").includes(contract.monthCode as FuturesMonthCode),
        )
        .map((contract) => contractChoice(contract, instruments[1])),
      overview: {
        pairId: request.pairPresetId,
        pairShort: pairLabels.short,
        pairLabel: preset.label,
        primaryMetricLabel: metricLabel(diagnostics.selectedMetric),
        primaryMetricValue: formatNumber(latestMetric, 4),
        seasonalZ: latestSeasonalZ,
        localZ: latestLocalZ,
        returnCorrelation: correlation,
        beta,
        adfStatistic: latestAvailable(diagnostics.adfResidual)?.statistic ?? null,
        adfProbabilityBand: adfBand,
        adfExactPValue: null,
        cointegrationStatistic: latestAvailable(diagnostics.engleGranger)?.statistic ?? null,
        cointegrationProbabilityBand: egBand,
        cointegrationExactPValue: null,
        stationarityCaveat: "ADF tests residual stationarity; Engle–Granger tests cointegration. Probability bands are approximate critical-value ranges, not exact p-values.",
        halfLifeDays: halfLife,
        ...(diagnostics.halfLife.status === "not-available"
          ? {
              halfLifeReason: adfSupportsStationarity && egSupportsStationarity
                ? diagnostics.halfLife.detail
                : "ADF and Engle–Granger do not both support a stationary spread at the 5% level.",
            }
          : {}),
        stability,
        signal,
        quality,
        qualityDetail: `Source coverage ${coverage.qualityStatus}; ${currentEpisode.points.length} same-date actual settlements; ${currentEpisode.excludedCount} excluded. ADF band ${adfBand ?? "N/A"}; EG band ${egBand ?? "N/A"}; exact p-values are not claimed.`,
      },
      spread: {
        points: spreadPoints,
        eligibleStart: spreadPoints.find((point) => point.eligible)?.date ?? currentEpisode.dateStart,
        displayStart: currentEpisode.dateStart,
        metricLabel: metricLabel(diagnostics.selectedMetric),
        unit,
        metrics,
        ...(decompositionByTemplate ? { decompositionByTemplate } : {}),
      },
      seasonality,
      signalLab: {
        strategy,
        metrics: [
              dashboardMetric("Net P/L", formatCurrency(diagnostics.backtest.metrics.cumulativePnl), "actual-contract settlements"),
              dashboardMetric("Trades", String(diagnostics.backtest.metrics.numberOfTrades), `entry ${request.controls.entryZ}; exit ${request.controls.exitZ}`),
              dashboardMetric("Turnover", formatNumber(diagnostics.backtest.metrics.turnover, 2), "entry, exit, and explicit roll executions"),
              dashboardMetric("Estimated costs", formatCurrency(diagnostics.backtest.metrics.estimatedTransactionCosts), "execution provenance retained per event"),
            ],
        forwardStudy,
        costAssumption: formalWalkForward
          ? costDescription
          : `${costDescription}. Latest-release research backtest; not a point-in-time revision reconstruction.`,
      },
      regime: {
        rolling,
        windows,
        leadLag,
        qualityNotes: [
          ...(formalWalkForward
            ? [
                "All formal results use unadjusted actual-contract episodes and same-date intersections.",
                `Input cutoff: ${asOfTimestamp(request.asOf)}; no forward fill or later revision is admitted.`,
              ]
            : [
                "Current-knowledge descriptive view from unadjusted actual-contract episodes; it is not a historical point-in-time reconstruction.",
                "Latest-only source-warning rows are retained in this descriptive view; the source-warning exclusion control applies only to formal point-in-time analysis.",
                "Backtest signals use prefix-only calculations on the latest released historical settlements; later revision availability is not reconstructed.",
              ]),
          `ADF probability band: ${adfBand ?? "N/A"}; Engle-Granger probability band: ${egBand ?? "N/A"}; exact p-values are null.`,
          diagnostics.windowSensitivity.stabilityDetail,
        ],
        stationarityCaveat: "ADF tests residual stationarity; Engle–Granger tests cointegration. Bands show approximate probability ranges from critical values; exact p-values are not calculated.",
      },
    };
  }

  private projectRegime(
    episode: EpisodeBundle,
    diagnostics: ProjectionDiagnostics,
    request: DashboardAnalysisRequest,
    config: AnalysisConfig,
  ): readonly RegimePoint[] {
    const rollingWindow = request.controls.window;
    if (episode.points.length < Math.max(config.minimumSample, rollingWindow)) return [];
    const indices = episode.points
      .map((_, index) => index)
      .filter((index) => index >= rollingWindow - 1);
    const baselineByDate = new Map(diagnostics.seasonality.baseline.map((point) => [point.date, point]));
    let previousBeta: number | null = null;
    return indices.flatMap((endIndex): RegimePoint[] => {
      const points = episode.points.slice(endIndex - rollingWindow + 1, endIndex + 1);
      const prefixAnalysis = analyzeActualEpisode({
        kind: "actual-episode",
        pair: episode.pair,
        episodeId: `${episodeId(episode.pair)}:through:${points.at(-1)!.date}`,
        source: "actual-settlements",
        backAdjusted: false,
        points,
      }, {
        pairPresetId: request.pairPresetId,
        metric: diagnostics.selectedMetric,
        minimumSample: config.minimumSample,
        zWindow: rollingWindow,
        volatilityWindow: Math.min(20, points.length),
      });
      const stationarity = this.stationarity(
        { ...episode, points },
        prefixAnalysis,
        rollingWindow,
      );
      const date = points.at(-1)!.date;
      const prefixBeta = latestAvailable(prefixAnalysis.betaResidual)?.beta ?? null;
      const correlation = latestAvailable(prefixAnalysis.returnCorrelation);
      const adf = latestAvailable(stationarity.adf);
      const eg = latestAvailable(stationarity.eg);
      const halfLife = latestAvailable(stationarity.halfLife);
      const volatility = prefixAnalysis.spreadVolatility.at(-1)?.value ?? null;
      const localZ = prefixAnalysis.localZ.at(-1)?.value ?? null;
      const metricValue = prefixAnalysis.metricSeries.status === "available"
        ? prefixAnalysis.metricSeries.value.points.at(-1)?.value ?? null
        : null;
      const baseline = baselineByDate.get(date);
      const seasonalZ = metricValue !== null && baseline?.status === "available"
        ? (metricValue - baseline.location) / baseline.dispersion
        : null;
      const betaStability = prefixBeta === null || previousBeta === null
        ? null
        : 100 / (1 + Math.abs(prefixBeta - previousBeta));
      if (prefixBeta !== null) previousBeta = prefixBeta;
      return [{
        date,
        correlation: correlation?.correlation ?? null,
        beta: prefixBeta,
        adfStatistic: adf?.statistic ?? null,
        adfProbabilityBand: adf?.approximateProbabilityBand ?? null,
        cointegrationStatistic: eg?.statistic ?? null,
        cointegrationProbabilityBand: eg?.approximateProbabilityBand ?? null,
        halfLife: halfLife?.halfLife ?? null,
        volatility,
        localZ,
        seasonalZ,
        betaStability,
        qualityScore: 100 * points.length / (points.length + episode.excludedCount),
      }];
    });
  }
}

function dashboardMetric(label: string, value: string, detail: string): DashboardMetric {
  return { label, value, detail };
}

function unavailableDashboardMetric(
  label: string,
  reason: string,
): DashboardMetric {
  return { label, value: "N/A", unavailableReason: reason, detail: reason };
}

function projectMatifDecomposition(
  diagnostics: ProjectionDiagnostics,
): PairDashboardFixture["spread"]["decompositionByTemplate"] | null {
  if (!diagnostics.matifDecomposition || !diagnostics.matifMaturity) return null;
  const template = diagnostics.matifMaturity.template;
  const fxMetric = diagnostics.matifFxAttribution?.status === "available"
    ? dashboardMetric(
        "FX contribution",
        formatNumber(diagnostics.matifFxAttribution.value.fxContribution, 2),
        "CAD/metric-tonne; midpoint-Shapley contribution to the latest MATIF CAD price change",
      )
    : unavailableDashboardMetric(
        "FX contribution",
        diagnostics.matifFxAttribution?.detail ?? "FX attribution is unavailable.",
      );
  if (diagnostics.matifDecomposition.status === "not-available") {
    const reason = diagnostics.matifDecomposition.detail;
    return {
      [template]: {
        template,
        metrics: [
          unavailableDashboardMetric("Executable spread", reason),
          unavailableDashboardMetric("Matched-maturity basis", reason),
          unavailableDashboardMetric("ICE curve contribution", reason),
          unavailableDashboardMetric("MATIF curve contribution", reason),
          fxMetric,
          dashboardMetric(
            "Maturity gap",
            `${diagnostics.matifMaturity.gapMonths} months`,
            diagnostics.matifMaturity.detail,
          ),
          dashboardMetric(
            "Maturity quality",
            diagnostics.matifMaturity.quality,
            diagnostics.matifMaturity.detail,
          ),
        ],
      },
    };
  }
  const decomposition = diagnostics.matifDecomposition.value;
  const matchedBasis = "matchedMaturityBasis" in decomposition
    ? decomposition.matchedMaturityBasis
    : "xxCrossMarketBasis" in decomposition
      ? decomposition.xxCrossMarketBasis
      : null;
  const matifCurve = "matifCurveContribution" in decomposition
    ? decomposition.matifCurveContribution
    : null;
  return {
    [template]: {
      template,
      metrics: [
        dashboardMetric("Executable spread", formatNumber(decomposition.executableSpread, 2), decomposition.unit),
        matchedBasis === null
          ? unavailableDashboardMetric("Matched-maturity basis", "The selected template has no defensible matched-maturity anchor.")
          : dashboardMetric("Matched-maturity basis", formatNumber(matchedBasis, 2), decomposition.unit),
        dashboardMetric("ICE curve contribution", formatNumber(decomposition.iceCurveContribution, 2), decomposition.unit),
        matifCurve === null
          ? unavailableDashboardMetric("MATIF curve contribution", "This template does not define a MATIF curve anchor.")
          : dashboardMetric("MATIF curve contribution", formatNumber(matifCurve, 2), decomposition.unit),
        fxMetric,
        dashboardMetric(
          "Maturity gap",
          `${diagnostics.matifMaturity.gapMonths} months`,
          diagnostics.matifMaturity.detail,
        ),
        dashboardMetric(
          "Maturity quality",
          diagnostics.matifMaturity.quality,
          diagnostics.matifMaturity.detail,
        ),
      ],
    },
  };
}

function metricLabel(metric: DisplayMetric): string {
  switch (metric) {
    case "raw-spread": return "Raw spread";
    case "ratio": return "Price ratio";
    case "log-ratio": return "Log price ratio";
    case "beta-residual": return "Beta-adjusted log residual";
  }
}

function formatNumber(value: number | null, digits: number): string {
  return value !== null && Number.isFinite(value) ? value.toFixed(digits) : "N/A";
}

function formatCurrency(value: number): string {
  return Number.isFinite(value)
    ? new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(value)
    : "N/A";
}

function contractChoice(contract: FuturesContract, instrument: Instrument): ContractChoice {
  if (!["F", "G", "H", "K", "N", "Q", "X", "Z"].includes(contract.monthCode)) {
    throw new Error(`Contract month ${contract.monthCode} is not enabled at the dashboard boundary.`);
  }
  const compact = `${contract.monthCode}${String(contract.deliveryYear).slice(-2)}`;
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return {
    id: contract.id,
    dataContractId: contract.id,
    exchange: instrument.exchange as ContractChoice["exchange"],
    product: instrument.product as ContractChoice["product"],
    monthCode: contract.monthCode as ContractChoice["monthCode"],
    deliveryMonth: monthNames[contract.deliveryMonth - 1] ?? String(contract.deliveryMonth),
    year: contract.deliveryYear,
    symbol: `${instrument.symbol}${compact}`,
    compact,
    label: `${instrument.exchange} ${instrument.product} ${monthNames[contract.deliveryMonth - 1] ?? contract.deliveryMonth} ${contract.deliveryYear} · ${instrument.symbol}${compact}`,
  };
}

function pairPresentation(pairId: DashboardAnalysisRequest["pairPresetId"]) {
  switch (pairId) {
    case "canola-soybean":
      return { short: "RS / ZS", legAName: "Canola", description: "Canola − Soybean relative value in CAD per metric tonne." };
    case "canola-soybean-oil":
      return { short: "ZL / RS", legAName: "Soy Oil", description: "Soy Oil − Canola relative value; no physical parity is implied." };
    case "canola-matif-rapeseed":
      return { short: "XR / RS", legAName: "Rapeseed", description: "Rapeseed − Canola cross-market basis with explicit EUR/CAD conversion." };
  }
}

function projectSpreadPoints(
  metricPoints: readonly { readonly date: ISODate; readonly value: number }[],
  localZ: readonly { readonly value: number | null }[],
  window: number,
): SpreadPoint[] {
  return metricPoints.map((point, index) => {
    const sample = metricPoints.slice(Math.max(0, index - window + 1), index + 1).map((value) => value.value);
    const center = mean(sample);
    const deviation = sample.length >= 2 ? sampleStandardDeviation(sample) : 0;
    const eligible = localZ[index]?.value !== null && localZ[index]?.value !== undefined;
    return {
      date: point.date,
      value: point.value,
      mean: center,
      upperOne: center + deviation,
      lowerOne: center - deviation,
      upperTwo: center + deviation * 2,
      lowerTwo: center - deviation * 2,
      zScore: localZ[index]?.value ?? null,
      eligible,
      quality: eligible ? "good" : "display-only",
      ...(index === 0
        ? { marker: "mapping-start" as const }
        : index === window - 1
          ? { marker: "eligible-start" as const }
          : {}),
    };
  });
}

function seasonalOrdinal(key: string): number {
  const [monthText, dayText] = key.split("-");
  const month = Number(monthText);
  const day = Number(dayText);
  const date = Date.UTC(2001, month - 1, day);
  return Math.round((date - Date.UTC(2001, 0, 1)) / 86_400_000);
}

function seasonalDistance(left: string, right: string): number {
  const direct = Math.abs(seasonalOrdinal(left) - seasonalOrdinal(right));
  return Math.min(direct, 365 - direct);
}

function projectSeasonality(
  episodes: readonly EpisodeAnalysisBundle[],
  diagnostics: ProjectionDiagnostics,
): PairDashboardFixture["seasonality"] {
  const currentYear = diagnostics.seasonality.currentContractYear;
  const current = episodes.find((episode) => episode.seasonal.contractYear === currentYear)!;
  const historical = episodes.filter((episode) => episode.seasonal.contractYear < currentYear);
  const series: SeasonalSeries[] = episodes.map((episode) => ({
    contractYear: episode.seasonal.contractYear,
    contracts: `${formatContract(episode.episode.pair.legA)} / ${formatContract(episode.episode.pair.legB)}`,
    current: episode.seasonal.contractYear === currentYear,
    points: episode.seasonal.points.map((point, seasonIndex) => ({
      seasonIndex,
      seasonLabel: toSeasonalDayKey(point.date) ?? "Feb-29 excluded",
      actualDate: point.date,
      value: point.value,
    })),
  }));
  const baselineByDate = new Map(diagnostics.seasonality.baseline.map((point) => [point.date, point]));
  const average: SeasonalAveragePoint[] = current.seasonal.points.flatMap((point, seasonIndex) => {
    const key = toSeasonalDayKey(point.date);
    const baseline = baselineByDate.get(point.date);
    if (!key || !baseline || baseline.status === "not-available") return [];
    const sample = historical.flatMap((episode) => episode.seasonal.points
      .filter((candidate) => {
        const candidateKey = toSeasonalDayKey(candidate.date);
        return candidateKey !== null && seasonalDistance(key, candidateKey) <= 10;
      })
      .map((candidate) => candidate.value));
    if (sample.length === 0) return [];
    return [{
      seasonIndex,
      seasonLabel: key,
      average: mean(sample),
      median: median(sample),
      p10: quantile(sample, 0.1),
      p25: quantile(sample, 0.25),
      p75: quantile(sample, 0.75),
      p90: quantile(sample, 0.9),
      current: point.value,
    }];
  });
  const window = getSeasonalityWindow(
    (getPairPreset(current.episode.pair.pairId).canolaLeg === "legA"
      ? current.episode.pair.legA.monthCode
      : current.episode.pair.legB.monthCode) as EnabledCanolaMonthCode,
    currentYear,
  );
  return {
    template: current.seasonal.templateId,
    windowLabel: `${window.start} to ${window.end}`,
    currentYear,
    series,
    average,
  };
}

function projectStrategy(backtest: DashboardAnalysisDiagnostics["backtest"]): StrategyPoint[] {
  let grossCumulative = 0;
  let peak = 100;
  return backtest.daily.map((point) => {
    grossCumulative += point.grossPnl;
    const grossIndex = 100 * (1 + grossCumulative / 1_000_000);
    peak = Math.max(peak, point.strategyReturnIndex);
    return {
      date: point.date,
      strategyIndex: point.strategyReturnIndex,
      grossIndex,
      drawdown: point.strategyReturnIndex - peak,
    };
  });
}

function weightedAverage(
  rows: readonly { readonly events: number; readonly value: number | null }[],
): number | null {
  const available = rows.filter((row) => row.events > 0 && row.value !== null);
  const denominator = available.reduce((sum, row) => sum + row.events, 0);
  return denominator === 0
    ? null
    : available.reduce((sum, row) => sum + row.value! * row.events, 0) / denominator;
}

function projectForwardStudy(
  study: DashboardAnalysisDiagnostics["forwardStudy"],
): ForwardStudyRow[] {
  return ([5, 10, 20, 40] as const).map((horizon) => {
    const rows = study.summaries.filter((row) => row.horizon === horizon);
    const sampleSize = rows.reduce((sum, row) => sum + row.events, 0);
    return {
      horizon: `${horizon}d`,
      meanChange: weightedAverage(rows.map((row) => ({ events: row.events, value: row.meanChange }))),
      medianChange: weightedAverage(rows.map((row) => ({ events: row.events, value: row.medianChange }))),
      reversionRate: weightedAverage(rows.map((row) => ({ events: row.events, value: row.reversionRate }))),
      winRate: weightedAverage(rows.map((row) => ({ events: row.events, value: row.winRate }))),
      mae: weightedAverage(rows.map((row) => ({ events: row.events, value: row.mae }))),
      mfe: weightedAverage(rows.map((row) => ({ events: row.events, value: row.mfe }))),
      sampleSize,
    };
  });
}

function projectWindows(
  result: DashboardAnalysisDiagnostics["windowSensitivity"],
): UiWindowSensitivityRow[] {
  return result.rows
    .filter((row) => UI_WINDOWS.includes(row.window as (typeof UI_WINDOWS)[number]))
    .map((row) => ({
      window: row.window as UiWindowSensitivityRow["window"],
      correlation: row.returnCorrelation,
      beta: row.beta,
      adfStatistic: latestAvailable(row.adfResidual)?.statistic ?? null,
      adfProbabilityBand: latestAvailable(row.adfResidual)?.approximateProbabilityBand ?? null,
      adfExactPValue: null,
      cointegrationProbabilityBand: latestAvailable(row.engleGranger)?.approximateProbabilityBand ?? null,
      cointegrationExactPValue: null,
      halfLife: latestAvailable(row.halfLife)?.halfLife ?? null,
      stability: row.sampleSize < row.window
        ? "overfit-risk"
        : row.engleGranger.status === "available" && row.adfResidual.status === "available"
          ? "stable"
          : "watch",
    }));
}

function shiftedForLag(
  points: readonly ActualEpisodePricePoint[],
  lag: -1 | 0 | 1,
): readonly ActualEpisodePricePoint[] {
  if (lag === 0) return points;
  return points.slice(1).map((point, index) => {
    const prior = points[index]!;
    return lag === -1
      ? { ...point, legB: prior.legB }
      : { ...point, legA: prior.legA };
  });
}

function projectLeadLag(points: readonly ActualEpisodePricePoint[]): LeadLagRow[] {
  return ([-1, 0, 1] as const).map((lag) => {
    const shifted = shiftedForLag(points, lag);
    const estimate = logReturnCorrelation(shifted, 10);
    return {
      alignment: lag === 0 ? "Same exchange-business date" : lag === -1 ? "Comparator leads one observation" : "Canola leads one observation",
      lag,
      returnCorrelation: latestAvailable(estimate)?.correlation ?? null,
      eligibleObservations: latestAvailable(estimate)?.returnObservations ?? 0,
      interpretation: estimate.status === "available"
        ? "Daily log-return correlation; observation shift is explicit and no holiday value is filled."
        : estimate.detail,
    };
  });
}
